import { useEffect, useMemo, useState } from "react";
import { useLoaderData, useParams, useRevalidator } from "react-router-dom";

import useMedicalCompleteLock from "../../../../../../hooks/useMedicalCompleteLock";
import useService from "../../../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../../../global/ApiRequestErrorHandler";
import ResponseModal from "../../../../../global/ResponseModal";
import Svg from "../../../../../ui library/components/svgs/standard/svg";
import HNSColors from "../../../../../ui library/HNSColors";
import ActionButtons from "../../../followup-form/components/ActionButtons";
import { ACTION_TYPE } from "../../utils/strings";

import AccordionContainer from "./components/AccordionContainer";
import RenderQuestion from "./components/RenderQuestion";
import { headerLabelIcon } from "./helpers/fieldOrderConfig";

const DynamicSection = () => {
  const formService = useService("repositories/follow-ups-form-submit-repository");
  const { revalidate } = useRevalidator();
  const { followUp, _case } = useLoaderData();
  const { actionItemId } = useParams();
  const { isAppointmentLocked } = useMedicalCompleteLock();
  const appointmentLocked = isAppointmentLocked(_case);
  const [followUpData, setFollowUpData] = useState(followUp); // Data from the backend
  const [formData, setFormData] = useState({}); // Data to send to the backend
  const [enableSave, setEnableSave] = useState(true);
  const [hasFormChanged, setHasFormChanged] = useState(false);
  const colorIcon = HNSColors.primary[500];
  const sxIcon = { width: "1.5rem" };
  const [errorMessage, setErrorMessage] = useState([]);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [resetValues, setResetValues] = useState(false);
  const [showActionButtons, setShowActionButtons] = useState(false);

  useEffect(() => {
    setFollowUpData(followUp);
    setShowActionButtons(!(followUp?.formHeader?.action_type === ACTION_TYPE.DATA_ENTRY_FOLLOW_UP));
  }, [followUp]);

  const cleanFollowUpData = useMemo(() => {
    return followUpData.sections.reduce((acc, section) => {
      Object.entries(section.questions).forEach(([key, question]) => {
        acc[key] = question.data;
      });
      return acc;
    }, {});
  }, [followUpData]);

  useEffect(() => {
    const hasChanges = (followUpData, formData) => {
      for (const key in formData) {
        if (Object.prototype.hasOwnProperty.call(followUpData, key)) {
          const followUpDataStringified = JSON.stringify(followUpData[key]);
          const formDataStringified = JSON.stringify(formData[key]);
          if (followUpDataStringified !== formDataStringified) {
            return true;
          }
        } else {
          return true;
        }
      }
      return false;
    };

    setHasFormChanged(hasChanges(cleanFollowUpData, formData));
  }, [formData]);

  if (!followUp?.sections) {
    return "No data found";
  }

  function changeDateFormat() {
    const { formName } = followUpData;
    function dateFormat(el) {
      el.referred_at = el?.referred_at?.split("T")[0];
    }
    if (formName === "Client Follow Up") {
      formData?.question_10?.map(dateFormat);
    }
    if (formName === "Provider Follow Up") {
      formData?.question_7?.map(dateFormat);
    }
  }

  async function handleSubmit() {
    changeDateFormat();

    try {
      await formService.createRecord(actionItemId, followUp.formId, formData);
      setResetValues(true);
      setEnableSave(true);
      setFormData({});
      setOpenSuccess(true);
      revalidate();
      setTimeout(() => {
        setResetValues(false);
      }, 500);
      return true;
    } catch (error) {
      const errArr = ApiRequestErrorHandler(error);
      const messagesErrArr = errArr.map((str) => str.replace(/question_[^:\s]*:?\s*/g, ""));
      setErrorMessage(messagesErrArr);
      setOpenError(true);
      return false;
    }
  }

  function handleAnswerChange(questionKey, newValue) {
    setFormData((prev) => {
      const updatedFormData = { ...prev };
      const originalValue = cleanFollowUpData[questionKey];

      // Handle empty email case (TODO: BE returns null)
      if (newValue?.liability_request_email === "") {
        newValue.liability_request_email = null;
      }

      // Determine if the value has changed
      const hasChanged = JSON.stringify(newValue) !== JSON.stringify(originalValue);

      // Handle different value types
      if (
        typeof newValue === "string" ||
        (typeof newValue === "object" && !Array.isArray(newValue))
      ) {
        if (!hasChanged) {
          delete updatedFormData[questionKey];
        } else {
          updatedFormData[questionKey] = newValue;
        }
      } else if (newValue === undefined || (Array.isArray(newValue) && newValue.length === 0)) {
        delete updatedFormData[questionKey];
      } else {
        updatedFormData[questionKey] = newValue;
      }

      return updatedFormData;
    });
  }

  return (
    <form
      id="dynamic-followup-form"
      className="mt-6 flex flex-wrap gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      {followUpData.sections.map(({ header, questions }) => {
        const sectionId = header.label.toLowerCase().replaceAll(" ", "-");
        return (
          <AccordionContainer
            id={sectionId}
            key={header.label}
            title={header.label}
            icon={
              /**
               *TODO: Remove headerLabelIcon when the backend is ready to provide icons
               */
              <Svg
                name={header.icon || headerLabelIcon[sectionId] || ""}
                color={colorIcon}
                sx={sxIcon}
              />
            }
          >
            {Object.entries(questions || {})
              .filter(([_, item]) => typeof item === "object" && item.module)
              .map(([questionKey, question], qIdx) => (
                <RenderQuestion
                  key={questionKey}
                  question={question}
                  index={qIdx}
                  sectionId={sectionId}
                  questionKey={questionKey}
                  handleAnswerChange={handleAnswerChange}
                  setEnableSave={setEnableSave}
                  resetValues={resetValues}
                  appointmentLocked={appointmentLocked}
                />
              ))}
          </AccordionContainer>
        );
      })}
      {showActionButtons && (
        <ActionButtons
          enableSave={enableSave && hasFormChanged}
          formData={formData}
          handleSubmit={handleSubmit}
        />
      )}
      <ResponseModal
        title={openError ? `${followUp?.formHeader?.action_type__label} Form` : "Succesfuly saved!"}
        description={"Changes were saved"}
        isError={openError}
        openBool={openError || openSuccess}
        setOpenBool={openError ? setOpenError : setOpenSuccess}
        errorMessage={errorMessage}
      />
    </form>
  );
};

export default DynamicSection;
